"use client"

import { useContext, useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { AuthContext } from "../../context/AuthContext"
import { useLanguage } from "../../context/LanguageContext"
import "./Navbar.css"

const Navbar = () => {
  const { isAuthenticated, logout, user, isAdmin } = useContext(AuthContext)
  const { language, changeLanguage, t, direction } = useLanguage()
  const navigate = useNavigate()

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Load theme preference from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("navbar-theme")
    if (savedTheme === "dark") {
      setIsDarkMode(true)
    }
  }, [])

  // Apply theme to navbar
  useEffect(() => {
    const navbar = document.querySelector(".navbar")
    if (navbar) {
      if (isDarkMode) {
        navbar.classList.add("dark-mode")
        localStorage.setItem("navbar-theme", "dark")
      } else {
        navbar.classList.remove("dark-mode")
        localStorage.setItem("navbar-theme", "light")
      }
    }
  }, [isDarkMode])

  const handleLogout = () => {
    logout()
    navigate("/")
    setIsMobileMenuOpen(false)
    setIsProfileDropdownOpen(false)
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen)
  }

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode)
  }

  const toggleLanguage = () => {
    const newLang = language === "en" ? "ar" : "en"
    changeLanguage(newLang)
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false)
  }

  const guestLinks = (
    <ul className="navbar-nav">
      <li className="nav-item">
        <Link to="/login" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-sign-in-alt"></i>
          <span>{t("Login") || "Login"}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link to="/register" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-user-plus"></i>
          <span>{t("Register") || "Register"}</span>
        </Link>
      </li>
    </ul>
  )

  const userLinks = (
    <ul className="navbar-nav">
      {isAdmin() && (
        <li className="nav-item">
          <Link to="/admin" className="nav-link" onClick={closeMobileMenu}>
            <i className="fas fa-cog"></i>
            <span>{t("Admin") || "Admin"}</span>
          </Link>
        </li>
      )}
      <li className="nav-item">
        <Link to="/dashboard" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-tachometer-alt"></i>
          <span>{t("Dashboard") || "Dashboard"}</span>
        </Link>
      </li>
      {/* <li className="nav-item">
        <Link to="/add-farm" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-plus-circle"></i>
          <span>{t("Add Farm") || "Add Farm"}</span>
        </Link>
      </li> */}
      <li className="nav-item">
        <Link to="/Groups" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-layer-group"></i>
          <span>{t("Station") || "Station"}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link to="/device-data" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-database"></i>
          <span>{t("Weather Parameter Device") || "Device Data"}</span>
        </Link>
      </li>
      <li className="nav-item">
        <Link to="/etref-history" className="nav-link" onClick={closeMobileMenu}>
          <i className="fas fa-history"></i>
          <span>{t("WATref History") || "WATref History"}</span>
        </Link>
      </li>

      {/* Profile Dropdown */}
      <li className="nav-item profile-dropdown">
        <button className="nav-link profile-btn" onClick={toggleProfileDropdown} aria-expanded={isProfileDropdownOpen}>
          <div className="profile-avatar">
            <i className="fas fa-user-circle"></i>
          </div>
          <span className="profile-name">{user?.name || "User"}</span>
          <i className={`fas fa-chevron-down dropdown-arrow ${isProfileDropdownOpen ? "open" : ""}`}></i>
        </button>

        <div className={`profile-dropdown-menu ${isProfileDropdownOpen ? "show" : ""}`}>
          <div className="profile-info">
            <div className="profile-avatar-large">
              <i className="fas fa-user-circle"></i>
            </div>
            <div className="profile-details">
              <span className="profile-name-large">{user?.name || "User"}</span>
              <span className="profile-email">{user?.email}</span>
            </div>
          </div>
          <div className="dropdown-divider"></div>
          <Link
            to="/profile"
            className="dropdown-item"
            onClick={() => {
              setIsProfileDropdownOpen(false)
              closeMobileMenu()
            }}
          >
            <i className="fas fa-user"></i>
            <span>{t("Profile Settings") || "Profile Settings"}</span>
          </Link>
          <Link
            to="/farm-management"
            className="dropdown-item"
            onClick={() => {
              setIsProfileDropdownOpen(false)
              closeMobileMenu()
            }}
          >
            <i className="fas fa-seedling"></i>
            <span>{t("My Farms") || "My Farms"}</span>
          </Link>
          <Link
            to="/crops"
            className="dropdown-item"
            onClick={() => {
              setIsProfileDropdownOpen(false)
              closeMobileMenu()
            }}
          >
            <i className="fas fa-leaf"></i>
            <span>{t("My Crops") || "My Crops"}</span>
          </Link>
          <div className="dropdown-divider"></div>
          <button onClick={handleLogout} className="dropdown-item logout-btn">
            <i className="fas fa-sign-out-alt"></i>
            <span>{t("Logout") || "Logout"}</span>
          </button>
        </div>
      </li>
    </ul>
  )

  return (
    <nav className={`navbar ${direction}`}>
      <div className="navbar-container">
        <Link to="/" className="navbar-brand">
          <i className="fas fa-cloud"></i>
          <span className="brand-text">{t("WATref Calculator") || "WATref Calculator"}</span>
        </Link>

        {/* Theme and Language Controls */}
        <div className="navbar-controls">
          {/* Theme Toggle */}
          <button
            className="control-btn theme-toggle"
            onClick={toggleTheme}
            title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
          >
            <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
          </button>

          {/* Language Toggle */}
          <button
            className="control-btn language-toggle"
            onClick={toggleLanguage}
            title={language === "en" ? "Switch to Arabic" : "Switch to English"}
          >
            <span className="language-text">{language === "en" ? "عربي" : "EN"}</span>
          </button>
        </div>

        {/* Mobile menu toggle */}
        <button className="navbar-toggle" onClick={toggleMobileMenu} aria-label="Toggle navigation menu">
          <span className={`hamburger ${isMobileMenuOpen ? "open" : ""}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>

        <div className={`navbar-links ${isMobileMenuOpen ? "active" : ""}`}>
          {isAuthenticated ? userLinks : guestLinks}
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileMenuOpen && <div className="mobile-overlay" onClick={closeMobileMenu}></div>}

      {/* Profile dropdown overlay */}
      {isProfileDropdownOpen && (
        <div className="dropdown-overlay" onClick={() => setIsProfileDropdownOpen(false)}></div>
      )}
    </nav>
  )
}

export default Navbar
